import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ListTodo, RefreshCw, AlertTriangle, Search, Eye, EyeOff } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import { confirmDialog } from '../../../lib/confirmStore';

export interface InternalTask {
  id: string;
  details: string;
  assigneeAdminId: string | null;
  assigneeName: string | null;
  linkedApplicationId: string;
  linkedUid: string;
  applicantName: string;
  stageStamp: string;
  stageStampLabel: string;
  sourceGateKey?: string | null;
  aiSuggested: boolean;
  status: 'pending' | 'completed' | 'canceled';
  createdAt: string;
  createdByAdminId: string;
  createdByName: string;
  completeBy: string | null;
  completedAt: string | null;
  /** Completion attribution (server-stamped on the →completed PATCH). */
  completedBy?: string | null;
  completedByName?: string | null;
  statusChangedBy?: string | null;
  statusChangedAt?: string | null;
  /** 'workflow' (drawer / Tasks page) or 'automation' (engine-created). */
  source?: 'workflow' | 'automation';
  closingNotes: string | null;
  workspaceId: string;
  currentStage: string | null;
  currentStageLabel: string | null;
  movedOn: boolean;
  underlyingResolved: boolean;
}

interface InternalTasksSectionProps {
  onOpenApplicant: (uid: string) => void;
  refreshSignal?: number;
}

const STAGE_FILTERS = [
  'draft', 'submitted', 'readyForScreening', 'readyForProcessing',
  'billings', 'immigration', 'preDeparture', 'closed',
];

const STAGE_SHORT: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  readyForScreening: 'Screening',
  readyForProcessing: 'Processing',
  billings: 'Billings',
  immigration: 'Immigration',
  preDeparture: 'Pre-Departure',
  closed: 'Closed',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Internal Tasks — admin-only task list on the Workflow page (never in the drawer).
 * Pending shown by default; completed/canceled hidden behind the status filter.
 */
export const InternalTasksSection: React.FC<InternalTasksSectionProps> = ({ onOpenApplicant, refreshSignal }) => {
  const [tasks, setTasks] = useState<InternalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('pending');
  const [stageFilter, setStageFilter] = useState('all');
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('status', statusFilter);
      if (stageFilter !== 'all') params.set('stageStamp', stageFilter);
      if (needsReviewOnly) params.set('needsReview', 'true');
      if (q.trim()) params.set('q', q.trim());
      const data: any = await safeFetch(`/api/admin/workflow/internal-tasks?${params.toString()}`);
      setTasks(data.tasks || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, stageFilter, needsReviewOnly, q]);

  useEffect(() => {
    const t = setTimeout(fetchTasks, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchTasks, refreshSignal]);

  const needsReviewCount = useMemo(() => tasks.filter((t) => t.movedOn || t.underlyingResolved).length, [tasks]);
  const detailTask = detailId ? tasks.find((t) => t.id === detailId) || null : null;

  return (
    <div className="shrink-0 bg-white border border-slate-200/80 rounded-2xl shadow-2xs overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
        <ListTodo className="h-4 w-4 text-amber-600" />
        <h2 className="text-sm font-extrabold text-slate-900 tracking-tight">
          Internal Tasks{' '}
          <span className="text-slate-400 font-bold tabular-nums">({tasks.length})</span>
        </h2>
        {needsReviewCount > 0 && (
          <span className="text-[10px] font-extrabold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
            {needsReviewCount} need review
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setNeedsReviewOnly((v) => !v)}
            aria-pressed={needsReviewOnly}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer ${needsReviewOnly ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {needsReviewOnly ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Needs review
          </button>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl px-2 py-1.5 text-xs font-bold text-slate-700 cursor-pointer"
            aria-label="Task status filter"
          >
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
            <option value="all">All statuses</option>
          </select>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl px-2 py-1.5 text-xs font-bold text-slate-700 cursor-pointer"
            aria-label="Stage stamp filter"
          >
            <option value="all">All stages</option>
            {STAGE_FILTERS.map((s) => (
              <option key={s} value={s}>{STAGE_SHORT[s]}</option>
            ))}
          </select>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tasks…"
              className="pl-8 pr-2 py-1.5 border border-slate-200 rounded-xl text-xs w-36"
            />
          </div>
          <button aria-label="Refresh tasks" title="Refresh tasks"
            type="button"
            onClick={fetchTasks}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-slate-500 hover:bg-slate-100 cursor-pointer"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="max-h-80 overflow-auto">
        {loading && tasks.length === 0 ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <AlertTriangle className="h-6 w-6 text-red-400 mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-600">{error}</p>
          </div>
        ) : tasks.length === 0 ? (
          <p className="p-6 text-center text-xs font-semibold text-slate-500">
            {statusFilter === 'pending' ? 'No pending tasks — all clear.' : 'No tasks match these filters.'}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {tasks.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setDetailId(t.id)}
                  className="w-full text-left flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50/70 cursor-pointer"
                >
                  <span
                    className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                      t.status === 'completed' ? 'bg-emerald-500 text-white'
                      : t.status === 'canceled' ? 'bg-slate-300 text-slate-600'
                      : 'bg-amber-400 text-white'
                    }`}
                  >
                    {t.status === 'completed' ? '✓' : t.status === 'canceled' ? '×' : '!'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-[13px] font-semibold text-slate-900 block leading-snug">{t.details}</span>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                      <span className="font-bold text-slate-700">{t.applicantName}</span>
                      <span className="font-mono">#{t.linkedApplicationId}</span>
                      <span className="uppercase tracking-wide font-bold text-slate-400">
                        stamped: {t.stageStampLabel}
                      </span>
                      {t.assigneeName && <span>→ {t.assigneeName}</span>}
                      {t.completeBy && <span>due {fmtDate(t.completeBy)}</span>}
                      {t.aiSuggested && (
                        <span className="text-[10px] font-extrabold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">AI</span>
                      )}
                    </span>
                    {t.movedOn && (
                      <span className="inline-block mt-1 text-[10px] font-extrabold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                        Applicant moved to {t.currentStageLabel} — review
                      </span>
                    )}
                    {t.underlyingResolved && (
                      <span className="inline-block mt-1 ml-1 text-[10px] font-extrabold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                        Underlying item looks resolved — safe to close?
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          onClose={() => setDetailId(null)}
          onOpenApplicant={onOpenApplicant}
          onChanged={fetchTasks}
        />
      )}
    </div>
  );
};

function TaskDetailModal({
  task,
  onClose,
  onOpenApplicant,
  onChanged,
}: {
  task: InternalTask;
  onClose: () => void;
  onOpenApplicant: (uid: string) => void;
  onChanged: () => void;
}) {
  const [closingNotes, setClosingNotes] = useState(task.closingNotes || '');
  const [busy, setBusy] = useState(false);

  const setStatus = async (status: 'pending' | 'completed' | 'canceled') => {
    if (status === 'completed' && !closingNotes.trim()) {
      notifyToast('warning', 'Closing notes are required to complete a task');
      return;
    }
    if (status === task.status) return;
    if (status === 'canceled' && !(await confirmDialog('Cancel this task?'))) return;
    setBusy(true);
    try {
      await safeFetch(`/api/admin/workflow/internal-tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, closingNotes: closingNotes.trim() || null }),
      });
      notifyToast('success', `Task marked ${status}`);
      onChanged();
      onClose();
    } catch (err: any) {
      notifyToast('error', err.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9400] flex items-center justify-center p-4 bg-slate-900/50" role="dialog" aria-modal="true" aria-label="Task details" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h3 className="text-sm font-extrabold text-slate-900">Internal task</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none cursor-pointer" aria-label="Close">×</button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm font-semibold text-slate-900 leading-snug">{task.details}</p>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div><dt className="font-bold text-slate-400 uppercase tracking-wide text-[10px]">Applicant</dt>
              <dd className="font-semibold text-slate-800">{task.applicantName} <span className="font-mono text-slate-500">#{task.linkedApplicationId}</span></dd></div>
            <div><dt className="font-bold text-slate-400 uppercase tracking-wide text-[10px]">Stage at creation</dt>
              <dd className="font-semibold text-slate-800">{task.stageStampLabel}</dd></div>
            <div><dt className="font-bold text-slate-400 uppercase tracking-wide text-[10px]">Assignee</dt>
              <dd className="font-semibold text-slate-800">{task.assigneeName || 'Unassigned'}</dd></div>
            <div><dt className="font-bold text-slate-400 uppercase tracking-wide text-[10px]">Complete by</dt>
              <dd className="font-semibold text-slate-800">{fmtDate(task.completeBy)}</dd></div>
            <div><dt className="font-bold text-slate-400 uppercase tracking-wide text-[10px]">Created</dt>
              <dd className="font-semibold text-slate-800">{fmtDate(task.createdAt)} by {task.createdByName}</dd></div>
            <div><dt className="font-bold text-slate-400 uppercase tracking-wide text-[10px]">Status</dt>
              <dd className="font-semibold text-slate-800 capitalize">{task.status}</dd></div>
            {task.status === 'completed' && (task.completedByName || task.completedAt) && (
              <div><dt className="font-bold text-slate-400 uppercase tracking-wide text-[10px]">Completed</dt>
                <dd className="font-semibold text-slate-800">
                  {fmtDate(task.completedAt)}
                  {task.completedByName ? ` by ${task.completedByName}` : ''}
                </dd></div>
            )}
          </dl>

          {task.movedOn && (
            <p className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              Applicant moved to {task.currentStageLabel} after this task was created. Review whether it is still relevant.
            </p>
          )}
          {task.underlyingResolved && (
            <p className="text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
              The underlying prerequisite looks resolved — safe to close this task?
            </p>
          )}

          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">
              Closing notes {task.status !== 'completed' && <span className="text-slate-400 normal-case font-medium">(required to complete)</span>}
            </label>
            <textarea
              value={closingNotes}
              onChange={(e) => setClosingNotes(e.target.value)}
              rows={3}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 resize-y"
              placeholder="What was done…"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {task.status !== 'completed' && (
              <button type="button" onClick={() => setStatus('completed')} disabled={busy} className="px-4 py-2.5 rounded-xl bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white text-xs font-bold cursor-pointer">
                {busy ? 'Saving…' : 'Mark completed'}
              </button>
            )}
            {task.status === 'pending' && (
              <button type="button" onClick={() => setStatus('canceled')} disabled={busy} className="px-4 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 text-xs font-bold cursor-pointer">
                Cancel task
              </button>
            )}
            {task.status !== 'pending' && (
              <button type="button" onClick={() => setStatus('pending')} disabled={busy} className="px-4 py-2.5 rounded-xl bg-amber-100 hover:bg-amber-200 disabled:opacity-50 text-amber-800 text-xs font-bold cursor-pointer">
                Reopen
              </button>
            )}
            {task.linkedUid && (
              <button type="button" onClick={() => { onClose(); onOpenApplicant(task.linkedUid); }} className="px-4 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold cursor-pointer">
                Open applicant
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
